import { ArrowBack } from '@mui/icons-material'
import { Link, LinkProps, styled } from '@mui/material'
import { NavLink, NavLinkProps, useNavigate } from 'react-router-dom'

/**
 * StyledNavLink component
 */
const StyledNavLink = styled(NavLink)(({ theme }) => ({
  textDecoration: 'none',
  color: theme.palette.primarySoft.main,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  width: 'fit-content'
}))

/** INavLinkBackProps type that defines the props for the NavLinkBack component */
type INavLinkBackProps = NavLinkProps

/**
 * NavLinkBack component
 * This component renders a styled navigation link that navigates back to the previous page.
 * It uses MUI's NavLink component and allows for custom styling.
 * @param {INavLinkBackProps} props - The properties for the navigation link component.
 * @returns {JSX.Element} The rendered navigation link component.
 * @example
 * <NavLinkBack to="/previous-page">Go Back</NavLinkBack>
 * @example
 * <NavLinkBack onClick={handleClick}>Go Back</NavLinkBack>
 * @example
 * <NavLinkBack title="Custom Title" onClick={handleClick}>Go Back</NavLinkBack>
 */
const NavLinkBack: React.FC<INavLinkBackProps> = props => {
  return (
    <StyledNavLink {...props}>
      <ArrowBack /> {props.title ?? 'Voltar'}
    </StyledNavLink>
  )
}

/**
 * INavGoBackProps interface that defines the props for the NavGoBack component.
 */
interface INavGoBackProps extends LinkProps {
  /** The title for the go back link */
  title?: string
}

/**
 * StyledLink component
 * This component styles the MUI Link component for navigation.
 * It customizes the link appearance and allows for custom styling.
 * @param {LinkProps} props - The properties for the link component.
 * @returns {JSX.Element} The rendered link component.
 * @example
 * <StyledLink to="/previous-page">Go Back</StyledLink>
 * @example
 * <StyledLink onClick={handleClick}>Go Back</StyledLink>
 */
const StyledLink = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  color: theme.palette.primarySoft.main,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  width: 'fit-content',
  cursor: 'pointer'
}))

/**
 * NavGoBack component
 * This component renders a link that navigates back to the previous page.
 * It uses MUI's Link component and allows for custom styling.
 * @param {INavGoBackProps} props - The properties for the navigation back component.
 * @returns {JSX.Element} The rendered navigation back component.
 * @example
 * <NavGoBack onClick={handleClick}>Go Back</NavGoBack>
 */
export const NavGoBack: React.FC<INavGoBackProps> = ({ onClick, ...props }) => {
  const navigate = useNavigate()
  return (
    <StyledLink
      {...props}
      onClick={event => {
        navigate(-1)
        if (onClick) onClick(event)
      }}
    >
      <ArrowBack /> {props.title ?? 'Voltar'}
    </StyledLink>
  )
}

export default NavLinkBack
